import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, UploadOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Card, Layout, Modal, Skeleton, Space, Typography, Upload, message } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AppLayout from "@/Components/Common/AppLayout";
import { logger } from "@/Services/Logger/Index";

const { Content } = Layout;
const { Text, Title } = Typography;

const ProfilePhotoUpload = ({
  resumeId: propsResumeId,
  onPhotoUploaded,
  currentPhotoUrl: propsPhotoUrl,
  open: controlledOpen,
  onClose,
  trigger,
  showModal = true,
  mode = "upload", // "upload" or "edit"
  renderMode = "modal", // "modal", "inline", or "page"
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const fetch = useAuthenticatedFetch();

  // For page mode, get resumeId from URL params
  const resumeId = renderMode === "page" ? params.resumeId : propsResumeId;

  const [loading, setLoading] = useState(renderMode === "page");
  const [uploading, setUploading] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(propsPhotoUrl || null);
  const [modalVisible, setModalVisible] = useState(false);

  // Load resume data when in page mode
  useEffect(() => {
    if (renderMode === "page") {
      let isMounted = true;

      const loadResumeData = async () => {
        if (!resumeId) {
          message.error("Resume ID is missing");
          navigate("/app/dashboard");
          return;
        }

        try {
          setLoading(true);
          const response = await fetch.get(`/resume/${resumeId}`);

          if (!isMounted) return;

          if (response.success && response.data) {
            setResumeData(response.data);
            setPhotoPreview(response.data?.profilePhoto?.url || null);
          } else {
            message.error("Failed to load resume data");
            navigate("/app/dashboard");
          }
        } catch (error) {
          if (!isMounted) return;
          logger.error("Error loading resume:", error);
          message.error("Failed to load resume data");
          navigate("/app/dashboard");
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };

      loadResumeData();

      return () => {
        isMounted = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId, renderMode]);

  // Update photo preview when currentPhotoUrl changes
  useEffect(() => {
    if (renderMode !== "page") {
      setPhotoPreview(propsPhotoUrl || null);
    }
  }, [propsPhotoUrl, renderMode]);

  // Handle controlled open state
  useEffect(() => {
    if (controlledOpen !== undefined) {
      setModalVisible(controlledOpen);
    }
  }, [controlledOpen]);

  // Determine if modal should be shown
  const isModalVisible = controlledOpen !== undefined ? controlledOpen : modalVisible;

  // Determine if we're in edit mode
  const isEditMode = mode === "edit" || (propsPhotoUrl && photoPreview) || photoPreview;

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }

    const isLt1M = file.size / 1024 / 1024 < 1;
    if (!isLt1M) {
      message.error("Image must be smaller than 1MB!");
      return false;
    }

    return true;
  };

  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;

    // Validate file
    if (!beforeUpload(file)) {
      return;
    }

    // Check if resumeId is provided
    if (!resumeId) {
      message.error("Resume ID is missing");
      onError(new Error("Resume ID is missing"));
      return;
    }

    setUploading(true);

    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
      // Send base64 data to backend
      const uploadData = {
        resumeId: resumeId,
        photoBase64: base64,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      };

      const result = await fetch.post("/resume/upload-profile-photo", uploadData, true);

      if (result.success || result.code === 200) {
        const successMessage = isEditMode ? "Profile photo updated successfully!" : "Photo uploaded successfully!";
        message.success(successMessage);
        setPhotoPreview(result.data.photoUrl);

        // Update resume data in page mode
        if (renderMode === "page" && resumeData) {
          setResumeData({
            ...resumeData,
            profilePhoto: {
              ...resumeData.profilePhoto,
              url: result.data.photoUrl,
            },
          });
        }

        // Close modal after successful upload/update
        if (showModal && renderMode === "modal") {
          setModalVisible(false);
          if (onClose) {
            onClose();
          }
        }

        // Call parent callback with the photo URL
        if (onPhotoUploaded) {
          onPhotoUploaded(result.data.photoUrl);
        }

        onSuccess(result.data);
      } else {
        throw new Error(result.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Error response:", error.response?.data);
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to upload photo. Please try again.";
      message.error(errorMessage);
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const handleOpenModal = () => {
    setModalVisible(true);
  };

  const handleDeletePhoto = async () => {
    if (!resumeId) {
      message.error("Resume ID is missing");
      return;
    }

    try {
      // Call backend to delete photo
      const result = await fetch.post("/resume/delete-profile-photo", { resumeId }, true);

      if (result.success || result.code === 200) {
        message.success("Profile photo deleted successfully!");
        setPhotoPreview(null);

        // Update resume data in page mode
        if (renderMode === "page" && resumeData) {
          setResumeData({
            ...resumeData,
            profilePhoto: null,
          });
        }

        if (showModal && renderMode === "modal") {
          setModalVisible(false);
          if (onClose) {
            onClose();
          }
        }

        if (onPhotoUploaded) {
          onPhotoUploaded(null);
        }
      } else {
        throw new Error(result.message || "Delete failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      message.error(error.response?.data?.message || error.message || "Failed to delete photo. Please try again.");
    }
  };

  const handleBack = () => {
    navigate(`/app/resume/${resumeId}/preview`);
  };

  // Content component (reusable for both modal and standalone)
  const uploadContent = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
        padding: isEditMode ? "32px 24px" : "24px",
        backgroundColor: isEditMode ? "#ffffff" : "#f8fafc",
        borderRadius: "12px",
        border: isEditMode ? "none" : "1px solid #e2e8f0",
      }}
    >
      {/* Photo Preview - Larger for edit mode */}
      <div
        style={{
          width: isEditMode ? "160px" : "120px",
          height: isEditMode ? "160px" : "120px",
          borderRadius: "50%",
          overflow: "hidden",
          backgroundColor: "#e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: isEditMode ? "4px solid #1890ff" : "3px solid #1890ff",
          boxShadow: isEditMode ? "0 4px 12px rgba(24, 144, 255, 0.15)" : "none",
        }}
      >
        {photoPreview ? (
          <img
            src={photoPreview}
            alt="Profile"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <UserOutlined style={{ fontSize: isEditMode ? "64px" : "48px", color: "#8c8c8c" }} />
        )}
      </div>

      {/* Instructions - Different for edit mode */}
      <div style={{ textAlign: "center", maxWidth: "400px" }}>
        <Text strong style={{ display: "block", marginBottom: "8px", fontSize: isEditMode ? "18px" : "16px" }}>
          {isEditMode ? "Update Your Profile Photo" : "Upload Your Profile Photo"}
        </Text>
        {!isEditMode && (
          <Text
            type="secondary"
            style={{ fontSize: "13px", display: "block", marginBottom: "8px", lineHeight: "1.5" }}
          >
            Your photo will be used to generate professional, visually appealing resumes.
          </Text>
        )}
        <Text type="secondary" style={{ fontSize: "12px" }}>
          Max size: 1MB • Formats: JPG, JPEG, PNG
        </Text>
      </div>

      {/* Action Buttons - Side by side for edit mode */}
      {isEditMode && photoPreview ? (
        <Space size="middle" style={{ width: "100%", maxWidth: "400px", justifyContent: "center" }}>
          <Upload
            customRequest={handleUpload}
            showUploadList={false}
            accept="image/jpeg,image/jpg,image/png"
            beforeUpload={beforeUpload}
          >
            <Button
              icon={<EditOutlined />}
              loading={uploading}
              type="primary"
              size="large"
              style={{
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
                height: "44px",
                minWidth: "160px",
                fontSize: "15px",
                fontWeight: "500",
                boxShadow: "0 2px 8px rgba(24, 144, 255, 0.2)",
              }}
            >
              {uploading ? "Uploading..." : "Change Photo"}
            </Button>
          </Upload>
          <Button
            icon={<DeleteOutlined />}
            danger
            size="large"
            onClick={handleDeletePhoto}
            style={{
              height: "44px",
              minWidth: "160px",
              fontSize: "15px",
              fontWeight: "500",
              boxShadow: "0 2px 8px rgba(255, 77, 79, 0.2)",
            }}
          >
            Remove Photo
          </Button>
        </Space>
      ) : (
        <Upload
          customRequest={handleUpload}
          showUploadList={false}
          accept="image/jpeg,image/jpg,image/png"
          beforeUpload={beforeUpload}
        >
          <Button
            icon={<UploadOutlined />}
            loading={uploading}
            type="primary"
            size="large"
            style={{
              backgroundColor: "#1890ff",
              borderColor: "#1890ff",
              height: "44px",
              minWidth: "200px",
              fontSize: "15px",
              fontWeight: "500",
              boxShadow: "0 2px 8px rgba(24, 144, 255, 0.2)",
            }}
          >
            {uploading ? "Uploading..." : photoPreview ? "Change Photo" : "Upload Photo"}
          </Button>
        </Upload>
      )}
    </div>
  );

  // PAGE MODE: Render full page layout
  if (renderMode === "page") {
    if (loading) {
      return (
        <AppLayout>
          <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
            <Content style={{ padding: "40px", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </Content>
          </Layout>
        </AppLayout>
      );
    }

    return (
      <AppLayout>
        <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
          <Content
            style={{
              padding: "24px",
              maxWidth: "1200px",
              margin: "0 auto",
              width: "100%",
              position: "relative",
            }}
          >
            {/* Header with back button */}
            <div
              style={{
                position: "absolute",
                top: "20px",
                left: "80px",
                zIndex: 10,
              }}
            >
              <Button
                type="default"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                className="back-to-options-btn"
                style={{
                  fontSize: "14px",
                  borderColor: "#1890ff",
                  color: "#1890ff",
                  backgroundColor: "transparent",
                  height: "36px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  borderRadius: "6px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.3s ease",
                }}
              >
                Back
              </Button>
            </div>

            <Space direction="vertical" size="large" style={{ width: "100%", marginTop: "60px" }}>
              {/* Main Content Card - Modal Style */}
              <Card
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
                bodyStyle={{ padding: "40px" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "24px",
                  }}
                >
                  {/* Page Title */}
                  <Title level={3} style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                    {photoPreview ? "Edit Your Profile Photo" : "Add Your Profile Photo"}
                  </Title>

                  {/* Upload Content */}
                  {uploadContent}
                </div>
              </Card>
            </Space>
          </Content>
        </Layout>
      </AppLayout>
    );
  }

  // INLINE MODE: If showModal is false, return just the content (backward compatibility)
  if (!showModal || renderMode === "inline") {
    return uploadContent;
  }

  // MODAL MODE: If trigger is provided, render trigger button + modal
  if (trigger) {
    return (
      <>
        {trigger === true ? (
          <Button
            type="default"
            icon={<UserOutlined />}
            onClick={handleOpenModal}
            style={{
              borderColor: "#1890ff",
              color: "#1890ff",
              height: "36px",
              fontSize: "14px",
              paddingLeft: "16px",
              paddingRight: "16px",
              borderRadius: "6px",
              fontWeight: "500",
            }}
          >
            {photoPreview ? "Edit Photo" : "Add Photo"}
          </Button>
        ) : (
          <div onClick={handleOpenModal}>{trigger}</div>
        )}
        <Modal
          title={isEditMode ? "Edit Your Profile Photo" : "Upload Your Profile Photo"}
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={null}
          width={600}
          centered
        >
          <div style={{ padding: "20px 0" }}>{uploadContent}</div>
        </Modal>
      </>
    );
  }

  // MODAL MODE (Default): render modal with content
  return (
    <Modal
      title={isEditMode ? "Edit Your Profile Photo" : "Upload Your Profile Photo"}
      open={isModalVisible}
      onCancel={handleModalClose}
      footer={null}
      width={600}
      centered
    >
      <div style={{ padding: "20px 0" }}>{uploadContent}</div>
    </Modal>
  );
};

export default ProfilePhotoUpload;
